import { useEffect, useRef, useState } from 'react';
import { Button, Input, Textarea } from '@artosera/ui';
import { useTranslation } from 'react-i18next';
import { boiteApi, type Conversation, type Message } from '../../api/boite-reception.js';
import { useShellContext } from '../shell/context.js';
import { galerieAModule } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { useGesteBoite } from './useGesteBoite.js';
import styles from './Boite.module.css';
export function Reponse({
  conversation,
  brouillon,
  presentationBrouillon,
}: {
  conversation: Conversation;
  brouillon?: Message;
  presentationBrouillon?: string;
}) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const editeur = useRef<HTMLTextAreaElement>(null);

  const presente = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      !presentationBrouillon ||
      brouillon?.id !== presentationBrouillon ||
      presente.current === presentationBrouillon ||
      !editeur.current
    )
      return;
    presente.current = presentationBrouillon;
    editeur.current.focus({ preventScroll: true });
    editeur.current.scrollIntoView({ block: 'center' });
  }, [brouillon?.id, presentationBrouillon]);

  const [texte, setTexte] = useState(brouillon?.texte ?? '');
  const [pieces, setPieces] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const composer = useGesteBoite((geste: 'brouillon' | 'envoyer', appel) =>
    boiteApi.composer(
      galerieSlug,
      `/conversations/${conversation.id}/${geste}`,
      { version: conversation.version, texte },
      pieces,
      appel,
    ),
  );
  const noter = useGesteBoite((_: void, appel) =>
    boiteApi.geste(
      galerieSlug,
      `/conversations/${conversation.id}/notes`,
      { version: conversation.version, texte: note },
      appel,
    ),
  );
  const piecesValides =
    pieces.length <= 5 && pieces.reduce((s, p) => s + p.size, 0) <= 10 * 1024 * 1024;
  const [retour, setRetour] = useState('');
  async function soumettre(geste: 'brouillon' | 'envoyer') {
    if (!piecesValides) return;
    const resultat = await composer.executer(geste);
    if (!resultat) return;
    setRetour(
      t(
        geste === 'brouillon'
          ? 'boiteReception.brouillonEnregistre'
          : 'boiteReception.envoiTermine',
      ),
    );
    if (resultat.message.etat === 'envoye') {
      setTexte('');
      setPieces([]);
    }
  }
  return (
    <div className={styles.editeurs}>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre('envoyer');
        }}
      >
        <h3>{t('boiteReception.repondre')}</h3>
        {galerieAModule(galerie, 'assistant-reponse') && brouillon?.assistant && (
          <section>
            <p>{t('assistantReponse.prepare')}</p>
            <h4>{t('assistantReponse.aVerifier')}</h4>
            <ul>
              {brouillon.assistant.aVerifier.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          </section>
        )}
        <Field htmlFor="reponse-texte" label={t('boiteReception.texte')}>
          <Textarea
            id="reponse-texte"
            ref={editeur}
            value={texte}
            maxLength={50000}
            required
            onChange={(e) => setTexte(e.target.value)}
          />
        </Field>
        {brouillon?.piecesJointes.length ? (
          <p>
            {t('boiteReception.piecesBrouillon', {
              noms: brouillon.piecesJointes.map((p) => p.nom).join(', '),
            })}
          </p>
        ) : null}
        <Field htmlFor="reponse-pieces" label={t('boiteReception.piecesLabel')}>
          <Input
            key={pieces.length === 0 ? 'vide' : 'plein'}
            id="reponse-pieces"
            type="file"
            multiple
            onChange={(e) => setPieces(Array.from(e.target.files ?? []))}
          />
        </Field>
        {!piecesValides ? <p role="alert">{t('boiteReception.limitePieces')}</p> : null}
        <div className={styles.gestes}>
          <Button
            type="button"
            variant="secondary"
            disabled={!piecesValides}
            loading={composer.pending}
            onClick={() => void soumettre('brouillon')}
          >
            {t('boiteReception.enregistrerBrouillon')}
          </Button>
          <Button
            type="submit"
            disabled={!texte.trim() || !piecesValides}
            loading={composer.pending}
          >
            {t('boiteReception.envoyer')}
          </Button>
        </div>
        {composer.erreur ? <p role="alert">{composer.erreur}</p> : null}
        {retour ? <p role="status">{retour}</p> : null}
      </form>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          void noter.executer().then((r) => {
            if (r) setNote('');
          });
        }}
      >
        <h3>{t('boiteReception.note')}</h3>
        <Field htmlFor="note-texte" label={t('boiteReception.noteInterne')}>
          <Textarea
            id="note-texte"
            value={note}
            maxLength={50000}
            required
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="secondary" disabled={!note.trim()} loading={noter.pending}>
          {t('boiteReception.ajouterNote')}
        </Button>
        {noter.erreur ? <p role="alert">{noter.erreur}</p> : null}
      </form>
    </div>
  );
}
